import React, { useState, useEffect } from "react";
import { Col, Button, Form, Dropdown, Modal, Alert, Spinner } from "react-bootstrap";
import { IoTimeOutline, IoEye } from "react-icons/io5";
import { FaEdit } from "react-icons/fa";
import { FcApproval } from "react-icons/fc";
import { TbCalendarCancel } from "react-icons/tb";
import { Link } from "react-router-dom";
import axios from "axios";
import "./leave.css";
import LeaveLayout from "../../components/LeaveLayout.jsx";

export default function PendingLeave() {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [showModal, setShowModal] = useState(false);           // Details modal
  const [modalData, setModalData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);

  // States for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(""); // "approved" or "rejected"
  const [confirmLeaveId, setConfirmLeaveId] = useState(null);
  const [notes, setNotes] = useState(""); // Yeh field backend ko bhejna hai

  const role = localStorage.getItem("role")?.trim().toLowerCase() || "hr";
  const token = localStorage.getItem("token");

  // Fetch pending leaves
  const fetchPendingLeaves = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get("https://backend.ems.hiradentallab.com/api/leavesmanual/pending", {
        params: { role },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPendingLeaves(response.data.data);
        setTotalEntries(response.data.data.length);
      } else {
        setAlert({ type: "danger", message: response.data.message });
      }
    } catch (error) {
      console.error("Error fetching pending leaves:", error);
      setAlert({ 
        type: "danger", 
        message: error.response?.data?.message || "Failed to load pending leaves" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingLeaves();
  }, [role]);

  // Search effect
  useEffect(() => {
    const filtered = pendingLeaves.filter((leave) =>
      (leave.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (leave.department || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
    setTotalEntries(filtered.length);
    setCurrentPage(1);
  }, [searchTerm, pendingLeaves]);

  // Open confirmation modal
  const handleOpenConfirm = (leaveId, action) => {
    setConfirmLeaveId(leaveId);
    setConfirmAction(action);
    setNotes("");
    setShowConfirmModal(true);
  };

  // Confirm and update status – **sirf notes bhejo, role backend handle karega**
  const handleConfirmAction = async () => {
    if (confirmAction === "rejected" && !notes.trim()) {
      setAlert({
        type: "danger",
        message: "Please provide reason/notes when rejecting."
      });
      return;
    }

    setUpdatingId(confirmLeaveId);
    setShowConfirmModal(false);

    try {
      const payload = {
        action: confirmAction,
        role: role,          // manager / hr / admin
        notes: notes.trim()  // ← Yeh sabse important! Backend isse sahi field mein daal dega
      };

      const response = await axios.put(
        `https://backend.ems.hiradentallab.com/api/leavesmanual/${confirmLeaveId}/status`,
        payload,
        { 
          headers: { 
            Authorization: `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          } 
        }
      );
      
      if (response.data.success) {
        setAlert({
          type: "success",
          message: `Leave ${confirmAction} successfully!`
        });
        
        fetchPendingLeaves();
      } else {
        setAlert({
          type: "danger",
          message: response.data.message || "Action failed"
        });
      }
    } catch (error) {
      console.error("Error updating leave status:", error);
      setAlert({
        type: "danger",
        message: error.response?.data?.message || "Failed to update leave status"
      });
    } finally {
      setUpdatingId(null);
      setNotes("");
    }
  };

  // Pagination helpers...
  const pageRange = () => {
    const totalPages = Math.ceil(totalEntries / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(totalEntries / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const handleShow = (data) => {
    setModalData(data);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const isApprover = ["hr", "admin"].includes(role);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
  };

  const getFilteredLeaves = () => {
    return pendingLeaves.filter((leave) =>
      (leave.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (leave.department || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredLeaves = getFilteredLeaves();
  const paginatedLeaves = filteredLeaves.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <LeaveLayout>
      <div>
        <h1 className="pending-leave-header">
          <IoTimeOutline className="pending-leave-icon" />
          Pending Leaves
        </h1>
        <hr />

        {alert && (
          <Alert variant={alert.type} onClose={() => setAlert(null)} dismissible>
            {alert.message}
          </Alert>
        )}

        <div className="pending-leave-content">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading pending leaves...</p>
            </div>
          ) : (
            <div>
              <div className="filter-container">
                <Col xs={12} md={6} className="filter-left">
                  <Dropdown className="d-inline mx-2">
                    <Dropdown.Toggle variant="primary">
                      Show {rowsPerPage} Entries
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="custom-dropdown-menu">
                      {[5, 10, 25].map((n) => (
                        <Dropdown.Item
                          key={n}
                          onClick={() => {
                            setRowsPerPage(n);
                            setCurrentPage(1);
                          }}
                        >
                          {n}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>

                <Col xs={12} md={6} className="filter-right">
                  <Form>
                    <Form.Control
                      type="search"
                      placeholder="Search by name or department..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form>
                </Col>
              </div>

              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="table-header">
                    <tr className="total-leave-table-header">
                      <th>SR</th>
                      <th>Name</th>
                      <th>Department</th>
                      <th>Allowance Type</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Days</th>
                      <th>Applied Date</th>
                      <th>Status</th>
                      <th>HR Action</th>
                      <th>Admin Action</th>
                      {isApprover && <th>Update Status</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedLeaves.length === 0 ? (
                      <tr>
                        <td colSpan={isApprover ? 13 : 12} className="text-center py-4">
                          No pending leaves available
                        </td>
                      </tr>
                    ) : (
                      paginatedLeaves.map((leave, index) => (
                        <tr key={leave._id}>
                          <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                          <td>{leave.name || leave.username}</td>
                          <td>{leave.department}</td>
                          <td>
                            <span className={`badge ${
                              leave.allowanceType === 'yearly' ? 'bg-info' :
                              leave.allowanceType === 'december' ? 'bg-warning' :
                              'bg-secondary'
                            }`}>
                              {leave.allowanceType === 'yearly' ? 'Yearly Allowance' :
                               leave.allowanceType === 'december' ? 'December Holiday' :
                               leave.allowanceType === 'simple' ? 'Simple Leave' :
                               'Unknown Type'}
                            </span>
                          </td>
                          <td>{formatDate(leave.startDate)}</td>
                          <td>{formatDate(leave.endDate)}</td>
                          <td>
                            <span className="badge bg-secondary">
                              {leave.includedDates?.length || 0} days
                            </span>
                          </td>
                          <td>{formatDate(leave.createdAt)}</td>
                          <td>
                            <span className={`badge ${
                              leave.status === 'pending' ? 'bg-warning' :
                              leave.status === 'approved' ? 'bg-success' : 'bg-danger'
                            }`}>
                              {leave.status}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              leave.hrAction === 'pending' ? 'bg-warning' :
                              leave.hrAction === 'approved' ? 'bg-success' : 'bg-danger'
                            }`}>
                              {leave.hrAction}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              leave.adminAction === 'pending' ? 'bg-warning' :
                              leave.adminAction === 'approved' ? 'bg-success' : 'bg-danger'
                            }`}>
                              {leave.adminAction}
                            </span>
                          </td>

                          {isApprover && (
                            <td>
                              {updatingId === leave._id ? (
                                <Spinner size="sm" animation="border" />
                              ) : (
                                <>
                                  <Button
                                    variant="success"
                                    size="sm"
                                    style={{ marginRight: "10px" }}
                                    onClick={() => handleOpenConfirm(leave._id, "approved")}
                                    disabled={
                                      (role === 'hr' && leave.hrAction !== 'pending') ||
                                      (role === 'admin' && leave.adminAction !== 'pending')
                                    }
                                  >
                                    <FcApproval /> Approve
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleOpenConfirm(leave._id, "rejected")}
                                    disabled={
                                      (role === 'hr' && leave.hrAction !== 'pending') ||
                                      (role === 'admin' && leave.adminAction !== 'pending')
                                    }
                                  >
                                    <TbCalendarCancel /> Reject
                                  </Button>
                                </>
                              )}
                            </td>
                          )}

                          <td>
                            <div className="action-button d-flex gap-2">
                              <Link to={`/edit-leave/${leave._id}`}>
                                <Button variant="warning" size="sm">
                                  <FaEdit />
                                </Button>
                              </Link>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleShow(leave)}
                              >
                                <IoEye />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filteredLeaves.length > 0 && (
                <div className="pagination-row">
                  <div className="entries-info">
                    Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                    {Math.min(currentPage * rowsPerPage, filteredLeaves.length)} of{" "}
                    {filteredLeaves.length} entries
                  </div>

                  <nav>
                    <ul className="pagination no-gap">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                          }}
                        >
                          Previous
                        </a>
                      </li>

                      {pageRange().map((page) => (
                        <li
                          key={page}
                          className={`page-item ${currentPage === page ? "active" : ""}`}
                        >
                          <a
                            href="/#"
                            className="page-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(page);
                            }}
                          >
                            {page}
                          </a>
                        </li>
                      ))}

                      <li
                        className={`page-item ${
                          currentPage === Math.ceil(filteredLeaves.length / rowsPerPage) ? "disabled" : ""
                        }`}
                      >
                        <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                          }}
                        >
                          Next
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Details Modal */}
        <Modal show={showModal} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Leave Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="leave-details">
              <p><strong>Name:</strong> {modalData.username}</p>
              <p><strong>Department:</strong> {modalData.department}</p>
              <p><strong>Allowance Type:</strong> {modalData.allowanceType === 'yearly' ? 'Yearly Allowance' :
               modalData.allowanceType === 'december' ? 'December Holiday' :
               modalData.allowanceType === 'simple' ? 'Simple Leave' :
               modalData.allowanceType || 'Unknown Type'}</p>
              <p><strong>Start Date:</strong> {formatDate(modalData.startDate)}</p>
              <p><strong>End Date:</strong> {formatDate(modalData.endDate)}</p>
              <p><strong>Total Days:</strong> {modalData.includedDates?.length || 0}</p>
              <p><strong>Reason:</strong></p>
              <div className="reason-box p-2 border rounded">
                {modalData.reason}
              </div>
              {modalData.includedDates && modalData.includedDates.length > 0 && (
                <>
                  <p className="mt-3"><strong>Included Dates:</strong></p>
                  <div className="dates-box p-2 border rounded">
                    {modalData.includedDates.map((date, idx) => (
                      <span key={idx} className="badge bg-light text-dark me-1 mb-1">
                        {formatDate(date)}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Confirmation Modal - Approve / Reject with Notes */}
        <Modal 
          show={showConfirmModal} 
          onHide={() => setShowConfirmModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {confirmAction === "approved" ? "Approve Leave" : "Reject Leave"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Please provide your notes / reason for{" "}
              <strong>{confirmAction === "approved" ? "approval" : "rejection"}</strong>:
            </p>
            <Form.Control
              as="textarea"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter your notes here..."
              className="mt-2"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant={confirmAction === "approved" ? "success" : "danger"}
              onClick={handleConfirmAction}
              disabled={confirmAction === "rejected" && !notes.trim()}
            >
              Confirm {confirmAction === "approved" ? "Approve" : "Reject"}
            </Button>
          </Modal.Footer>
        </Modal>

      </div>
    </LeaveLayout>
  );
}